import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import { getStoredImpersonation } from '@/lib/impersonation';
import { IDLE_TIMEOUT_MINUTES, recordSignOut } from '@/lib/signOutReason';

/** Default idle timeout, shared with the warning dialog and the login page's sign-out notice. */
const IDLE_TIMEOUT_MS = IDLE_TIMEOUT_MINUTES * 60 * 1000;
/** Warning shown 60 seconds before logout */
const WARNING_BEFORE_MS = 60 * 1000;

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  // `scroll` does not bubble, so scrolling inside a table, modal or the sidebar never reached `document`
  // and did not count. The wheel or trackpad gesture behind it does bubble.
  'wheel',
  'touchstart',
  'pointerdown',
];

/**
 * When the user last did something, in any tab. Logging out signs every tab out, so "idle" has to mean
 * idle in all of them. Each tab used to keep its own clock, and a tab left in the background signed the
 * user out of the one they were working in, 15 minutes after they last touched it.
 */
const LAST_ACTIVITY_KEY = 'isp_last_activity';

function readLastActivity(): number {
  try {
    return Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0;
  } catch {
    return 0;
  }
}

function recordActivity(at: number): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(at));
  } catch {
    // Storage unavailable: this tab keeps its own clock.
  }
}

/**
 * Monitors user activity and triggers a warning dialog before auto-logout.
 *
 * Activity in any tab counts. A Login-as-Partner tab still keeps its own clock: timing out there only
 * hands that one tab back to the staff session.
 *
 * Returns `{ showWarning, secondsLeft, dismissWarning }`:
 * - `showWarning`: true when the warning countdown is active
 * - `secondsLeft`: countdown seconds until forced logout
 * - `dismissWarning`: resets the timer (user clicked "Stay logged in")
 */
export function useIdleTimeout(timeoutMs = IDLE_TIMEOUT_MS) {
  const { isAuthenticated, logout } = useAuthStore();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const warningTimerRef = useRef<ReturnType<typeof setInterval>>();
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout>>();
  /** The moment this tab's idle clock counts from. */
  const idleSinceRef = useRef(Date.now());
  /** Lets the timers restart the clock without a dependency cycle with the callback that sets them. */
  const restartFromRef = useRef<(at: number) => void>(() => {});

  const clearAllTimers = useCallback(() => {
    clearTimeout(idleTimerRef.current);
    clearInterval(warningTimerRef.current);
    clearTimeout(logoutTimerRef.current);
  }, []);

  /** Activity another tab recorded after this tab's clock started, or null. */
  const activityElsewhere = useCallback((): number | null => {
    if (getStoredImpersonation()) return null;
    const at = readLastActivity();
    return at > idleSinceRef.current ? at : null;
  }, []);

  const handleLogout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    // Say why on the login page. A Login-as-Partner tab only hands back to the staff session instead.
    if (!getStoredImpersonation()) recordSignOut('idle');
    logout();
    // Navigate handled by auth guard in router
  }, [clearAllTimers, logout]);

  const startWarningCountdown = useCallback(() => {
    // Busy in another tab meanwhile: count from there instead of warning.
    const elsewhere = activityElsewhere();
    if (elsewhere) {
      restartFromRef.current(elsewhere);
      return;
    }

    const warningSeconds = Math.floor(WARNING_BEFORE_MS / 1000);
    setSecondsLeft(warningSeconds);
    setShowWarning(true);

    // Countdown every second
    warningTimerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(warningTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Force logout after warning period, unless another tab saw activity during it
    logoutTimerRef.current = setTimeout(() => {
      const later = activityElsewhere();
      if (later) restartFromRef.current(later);
      else handleLogout();
    }, WARNING_BEFORE_MS);
  }, [activityElsewhere, handleLogout]);

  const restartFrom = useCallback(
    (at: number) => {
      clearAllTimers();
      setShowWarning(false);

      if (!isAuthenticated) return;

      idleSinceRef.current = at;
      // Set idle timer: fires warning X seconds before logout
      idleTimerRef.current = setTimeout(
        startWarningCountdown,
        Math.max(0, at + timeoutMs - WARNING_BEFORE_MS - Date.now()),
      );
    },
    [clearAllTimers, isAuthenticated, timeoutMs, startWarningCountdown],
  );
  restartFromRef.current = restartFrom;

  const resetTimer = useCallback(() => {
    restartFrom(Date.now());
  }, [restartFrom]);

  const dismissWarning = useCallback(() => {
    recordActivity(Date.now());
    resetTimer();
  }, [resetTimer]);

  // Attach activity listeners
  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    // Throttle activity detection to avoid excessive resets
    let lastActivity = Date.now();
    const THROTTLE_MS = 30_000; // 30s

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivity < THROTTLE_MS) return;
      lastActivity = now;
      recordActivity(now);

      // Only reset if warning isn't already showing
      if (!showWarning) {
        resetTimer();
      }
    };

    // Activity another tab records restarts this tab's clock too, and closes its warning.
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== LAST_ACTIVITY_KEY) return;
      const at = activityElsewhere();
      if (at) restartFrom(at);
    };

    // Initial timer start
    resetTimer();

    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
    window.addEventListener('storage', handleStorage);

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('storage', handleStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return { showWarning, secondsLeft, dismissWarning };
}
